<template>
    <fieldset class="@container select-none">
        <div :class="radioGroupClasses">
            <div v-for="(option, index) in options" :key="option.value" class="flex items-center">
                <input
                    :id="`${componentId}-${option.value}`"
                    :name="componentId"
                    :checked="option.value === selection"
                    :disabled="option.disabled"
                    type="radio"
                    :class="optionClasses"
                    @change="selection = option.value"
                />
                <label :for="`${componentId}-${option.value}`" :class="labelClasses(option)">{{ option.label }}</label>
            </div>
        </div>
    </fieldset>
</template>

<script setup lang="ts">
    import { computed, useId } from 'vue'

    type Option = {
        value: string
        label: string
        disabled?: boolean
    }

    const props = withDefaults(
        defineProps<{
            id?: string
            label?: string
            size?: 'md'
            variant?: 'primary'
            options: Option[]
            orientation?: 'vertical' | 'horizontal'
        }>(),
        {
            id: undefined,
            label: '',
            size: 'md',
            variant: 'primary',
            options: () => [],
            orientation: 'vertical'
        }
    )

    const generatedId = useId()
    const componentId = computed(() => props.id || generatedId)

    /**
     * Check if selection is empty or disabled and set to first enabled option
     */
    const firstEnabledOption = props.options.find((option) => !option.disabled)
    const selection = defineModel<string>({ default: undefined, required: false })
    if (!selection.value) {
        selection.value = firstEnabledOption?.value ?? ''
    }
    if (props.options.find((option) => option.value === selection.value)?.disabled) {
        selection.value = firstEnabledOption?.value ?? ''
    }

    const optionClasses = computed(() => {
        const baseClasses =
            'relative block size-[22px] appearance-none rounded-full bg-gray-100 before:absolute before:inset-[7px] before:rounded-full before:bg-transparent cursor-pointer transition-all duration-100 hover:before:bg-gray-200 transition-all duration-100 before:transition-all before:duration-100 shrink-0'
        const checkedClasses = 'checked:border-green-500 checked:bg-green-500 checked:before:bg-system-dark hover:checked:before:bg-system-dark'
        const focusClasses = 'focus-visible:outline-3 focus-visible:outline-neutral-100'
        const disabledClasses = 'disabled:bg-gray-50 disabled:before:bg-gray-100 disabled:cursor-default disabled:pointer-events-none'

        return [baseClasses, checkedClasses, focusClasses, disabledClasses]
    })

    const labelClasses = (option: Option) => {
        const baseClasses = 'block pl-3'
        const activeClasses = 'cursor-pointer'
        const inactiveClasses = 'text-inactive cursor-default'

        return [baseClasses, option.disabled ? inactiveClasses : activeClasses]
    }

    const radioGroupClasses = computed(() => {
        const baseClasses = 'flex'
        const orientationOptions = {
            vertical: 'flex-col gap-4',
            horizontal: 'flex-row gap-6 @max-xs:flex-col'
        }

        return [baseClasses, orientationOptions[props.orientation]]
    })
</script>
